// Drawing: the island, territories, tracks, trains, stations and effects, from whatever state is current.
import { G, CFG, NAMES, PCOL, S, SHAPES, shapeOf, W, H, capOf, cellXY, clamp, dOf, distMap, ease, ensureGeom, lineNext, pointAt, rgba, shade, snapToLine, stationsOf, trainSeg, upgradeTarget, validSpot } from '../sim/game.js';
import { UI, me, myClaim } from './ui-state.js';

// ---------- rendering ----------
export const cvs = document.getElementById('c'), ctx = cvs.getContext('2d');
export let dpr = 1;
export const view = { s: 1, x: 0, y: 0 }, theme = {};
export function readTheme() {
  const cs = getComputedStyle(document.documentElement);
  for (const k of ['sea', 'paper', 'paper2', 'ink', 'ink2', 'border', 'danger', 'waterLine']) theme[k] = cs.getPropertyValue('--' + k).trim();
  theme.mapFont = cs.getPropertyValue('--display').trim() || 'sans-serif'; theme.dark = false;
}
export function resize() {
  dpr = window.devicePixelRatio || 1;
  cvs.width = innerWidth * dpr; cvs.height = innerHeight * dpr;
  cvs.style.width = innerWidth + 'px'; cvs.style.height = innerHeight + 'px';
  const wide = innerWidth > 1400, left = wide ? 262 : innerWidth > 760 ? 120 : 8, right = wide ? 232 : 8;
  const draft = document.getElementById('draft'), narrow = innerWidth <= 760;
  const drafting = draft && !draft.classList.contains('hidden') && G && G.phase === 'draft';
  // on a wide screen the setup card sits at the top: fit the island below it
  const top = narrow ? 50 : drafting ? draft.getBoundingClientRect().bottom + 12 : 22;
  const b = G && G.map ? G.map.bounds : { x: 0, y: 0, w: W, h: H };
  // on phones the setup card sits at the bottom: fit the island into the space above it
  const bottom = !narrow ? 68 : draft && !draft.classList.contains('hidden') ? draft.getBoundingClientRect().height + 24 : 130;
  const s = Math.min((innerWidth - left - right) / b.w, (innerHeight - top - bottom) / b.h);
  view.fit = { s, x: left + (innerWidth - left - right - b.w * s) / 2 - b.x * s, y: top + (innerHeight - top - bottom - b.h * s) / 2 - b.y * s };
  applyCam();
}
// camera: UI.cam overrides the fitted view once the player zooms or pans
export function applyCam() {
  const c = G && UI.cam ? UI.cam : view.fit;
  if (G && UI.cam) {
    const b = G.map.bounds, f = view.fit.s;
    c.s = clamp(c.s, f * 0.7, f * 5);
    const m = 150;
    c.x = clamp(c.x, m - (b.x + b.w) * c.s, innerWidth - m - b.x * c.s);
    c.y = clamp(c.y, m - (b.y + b.h) * c.s, innerHeight - m - b.y * c.s);
  }
  view.s = c.s; view.x = c.x; view.y = c.y;
}
function camFrom() { if (!UI.cam) UI.cam = { s: view.s, x: view.x, y: view.y }; return UI.cam; }
export function zoomAt(sx, sy, f) {
  const c = camFrom(), wx = (sx - c.x) / c.s, wy = (sy - c.y) / c.s;
  c.s = clamp(c.s * f, view.fit.s * 0.7, view.fit.s * 5);
  c.x = sx - wx * c.s; c.y = sy - wy * c.s;
  applyCam();
}
export function panBy(dx, dy) { const c = camFrom(); c.x += dx; c.y += dy; applyCam(); }
export function resetCam() { if (G) UI.cam = null; applyCam(); }
export function toWorld(e) { return { x: (e.clientX - view.x) / view.s, y: (e.clientY - view.y) / view.s }; }

function polyPath(c, x, y, r, n, rot) { for (let i = 0; i < n; i++) { const a = rot + i * Math.PI * 2 / n; i ? c.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r) : c.moveTo(x + Math.cos(a) * r, y + Math.sin(a) * r); } }
export function shapePath(c, shape, x, y, r) {
  c.beginPath();
  switch (shape) {
    case 'circle': c.arc(x, y, r, 0, Math.PI * 2); break;
    case 'square': { const s = r * 0.86; c.rect(x - s, y - s, 2 * s, 2 * s); break; }
    case 'triangle': polyPath(c, x, y + r * 0.18, r * 1.22, 3, -Math.PI / 2); break;
    case 'pentagon': polyPath(c, x, y + r * 0.06, r * 1.08, 5, -Math.PI / 2); break;
    case 'diamond': polyPath(c, x, y, r * 1.2, 4, -Math.PI / 2); break;
    case 'star': for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + i * Math.PI / 5, rr = i % 2 ? r * 0.55 : r * 1.28; i ? c.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr) : c.moveTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr); } break;
    case 'hexagon': polyPath(c, x, y, r * 1.1, 6, 0); break;
    case 'cross': { const a = r * 1.05, b = r * 0.38; c.moveTo(x - b, y - a); c.lineTo(x + b, y - a); c.lineTo(x + b, y - b); c.lineTo(x + a, y - b); c.lineTo(x + a, y + b); c.lineTo(x + b, y + b); c.lineTo(x + b, y + a); c.lineTo(x - b, y + a); c.lineTo(x - b, y + b); c.lineTo(x - a, y + b); c.lineTo(x - a, y - b); c.lineTo(x - b, y - b); break; }
    case 'heart': { const k = r * 1.08; c.moveTo(x, y + k); c.bezierCurveTo(x - k * 1.25, y + k * 0.1, x - k * 1.05, y - k * 0.95, x, y - k * 0.38); c.bezierCurveTo(x + k * 1.05, y - k * 0.95, x + k * 1.25, y + k * 0.1, x, y + k); break; }
    case 'crescent': { const k = r * 1.12; c.arc(x, y, k, 0.35 * Math.PI, 1.65 * Math.PI, false); c.arc(x + k * 0.55, y - k * 0.08, k * 0.82, 1.42 * Math.PI, 0.55 * Math.PI, true); break; }
  }
  c.closePath();
}
// outlines are turned into Path2D once per map version (the map only changes when land is annexed)
const PATHS = { key: null, land: null, regions: [] };
const toPath = loops => { const p = new Path2D(); for (const l of loops) { l.forEach((pt, i) => (i ? p.lineTo(pt.x, pt.y) : p.moveTo(pt.x, pt.y))); p.closePath(); } return p; };
function ensurePaths() {
  const key = G.map;
  if (PATHS.key === key && PATHS.mapV === G.mapV) return;
  PATHS.key = key; PATHS.mapV = G.mapV;
  PATHS.land = toPath(G.map.landLoops);
  PATHS.regions = G.regions.map(r => toPath(r.loops));
  PATHS.labels = G.regions.map(nameSpot);
}
// where a territory's name sits: deep inside it (clear of gates) and away from its factories
function nameSpot(r) {
  if (r.mergedInto != null) return null;
  const { own, df } = G.map, hearts = r.hearts || [r.c];
  let best = null, bs = -1;
  for (let k = 0; k < own.length; k++) {
    if (own[k] !== r.id || df[k] < 4) continue;
    const q = cellXY(k), dh = Math.min(...hearts.map(h => Math.hypot(h.x - q.x, h.y - q.y)));
    const s = Math.min(dh, 110) + df[k] * 3;
    if (s > bs) { bs = s; best = q; }
  }
  return best || r.c;
}
export function tracePath(geos) {
  ctx.beginPath();
  geos.forEach((g, i) => g.pts.forEach((q, j) => (i === 0 && j === 0 ? ctx.moveTo(q.x, q.y) : ctx.lineTo(q.x, q.y))));
}
// where two lines cross away from a station, the later one bridges over the earlier: a short gap in paper colour
// either side of it, the way a metro map shows lines passing over each other. Worked out once per track layout.
const XC = { key: '', list: [] };
function crossings() {
  const key = G.geoV + ':' + G.players.map(p => p.alive ? p.lines.length : 'x').join(',');
  if (XC.key === key) return XC.list;
  const segs = [], grid = new Map(), C = 48, list = [];
  let order = 0;
  for (const p of G.players) {
    if (!p.alive) continue;
    for (const L of p.lines) {
      order++;
      for (const g of L.geo || []) for (let j = 0; j + 1 < g.pts.length; j++) {
        const a = g.pts[j], b = g.pts[j + 1], sg = { a, b, L, p, order };
        const i0 = Math.floor(Math.min(a.x, b.x) / C), i1 = Math.floor(Math.max(a.x, b.x) / C), j0 = Math.floor(Math.min(a.y, b.y) / C), j1 = Math.floor(Math.max(a.y, b.y) / C);
        for (let i = i0; i <= i1; i++) for (let k = j0; k <= j1; k++) { const c = i + ',' + k; if (!grid.has(c)) grid.set(c, []); grid.get(c).push(segs.length); }
        segs.push(sg);
      }
    }
  }
  const stations = [...G.stations.values()], seen = new Set();
  for (const ids of grid.values()) for (let x = 0; x < ids.length; x++) for (let y = x + 1; y < ids.length; y++) {
    const s1 = segs[ids[x]], s2 = segs[ids[y]];
    if (s1.L === s2.L) continue;
    const pair = ids[x] < ids[y] ? ids[x] + '/' + ids[y] : ids[y] + '/' + ids[x];
    if (seen.has(pair)) continue; seen.add(pair);
    const q = hit(s1.a, s1.b, s2.a, s2.b); if (!q) continue;
    // only a real crossing gets a bridge: lines meeting at a shallow angle are running together, not crossing
    const ang1 = Math.atan2(s1.b.y - s1.a.y, s1.b.x - s1.a.x), ang2 = Math.atan2(s2.b.y - s2.a.y, s2.b.x - s2.a.x);
    if (Math.abs(Math.sin(ang1 - ang2)) < 0.42) continue;
    if (stations.some(st => Math.hypot(st.x - q.x, st.y - q.y) < 22)) continue;   // lines meeting at a stop are not crossing
    if (list.some(o => Math.hypot(o.x - q.x, o.y - q.y) < 8)) continue;
    const top = s1.order > s2.order ? s1 : s2, ang = Math.atan2(top.b.y - top.a.y, top.b.x - top.a.x);
    const low = top === s1 ? s2 : s1, under = (UI.selLine === low.L.id ? 7.5 : 6) / Math.max(0.3, Math.abs(Math.sin(ang1 - ang2)));
    list.push({ x: q.x, y: q.y, ang, L: top.L, p: top.p, under });
  }
  XC.key = key; XC.list = list;
  return list;
}
function hit(a, b, c, d) {
  const r = { x: b.x - a.x, y: b.y - a.y }, s = { x: d.x - c.x, y: d.y - c.y }, den = r.x * s.y - r.y * s.x;
  if (Math.abs(den) < 1e-9) return null;
  const t = ((c.x - a.x) * s.y - (c.y - a.y) * s.x) / den, u = ((c.x - a.x) * r.y - (c.y - a.y) * r.x) / den;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1 ? { x: a.x + r.x * t, y: a.y + r.y * t } : null;
}
function drawCrossings() {
  ctx.save(); ctx.lineCap = 'butt';
  for (const c of crossings()) {
    const dx = Math.cos(c.ang), dy = Math.sin(c.ang), w = UI.selLine === c.L.id ? 7.5 : 6;
    const seg = (len, width, style) => { ctx.beginPath(); ctx.moveTo(c.x - dx * len, c.y - dy * len); ctx.lineTo(c.x + dx * len, c.y + dy * len); ctx.lineWidth = width; ctx.strokeStyle = style; ctx.stroke(); };
    // a casing just wide enough to cut the line below, and only as long as that line is wide: a clean gap, no patch
    seg(c.under / 2 + 0.5, w + 5, theme.paper);
    seg(c.under / 2 + 3, w, c.L.color);
  }
  ctx.restore();
}
export function render() {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  if (UI.mode === 'title') return drawTitle(performance.now() / 1000);
  ctx.fillStyle = theme.sea; ctx.fillRect(0, 0, innerWidth, innerHeight);
  if (!G || !G.map) return;
  ensurePaths();
  ctx.setTransform(dpr * view.s, 0, 0, dpr * view.s, dpr * view.x, dpr * view.y);
  ctx.lineJoin = 'round'; ctx.lineCap = 'round';
  const t = performance.now() / 1000;
  const ink = a => rgba(theme.dark ? '#ffffff' : '#000000', a);

  // water-lining: fine rings echoing the coast out to sea, the way engraved charts draw shallow water
  for (const [d, a] of [[54, 0.08], [38, 0.11], [24, 0.15], [12, 0.2]]) {
    ctx.strokeStyle = rgba(theme.waterLine, a); ctx.lineWidth = d * 2 + 1.4; ctx.stroke(PATHS.land);
    ctx.strokeStyle = theme.sea; ctx.lineWidth = d * 2 - 1.4; ctx.stroke(PATHS.land);
  }
  ctx.fillStyle = theme.paper; ctx.fill(PATHS.land, 'evenodd');
  // paper grain over the land
  ctx.save(); ctx.clip(PATHS.land, 'evenodd'); ctx.fillStyle = grain(); ctx.fillRect(0, 0, W, H); ctx.restore();
  // territories: a wash of the owner's colour, deepening into a band along the inside of the border
  for (const r of G.regions) {
    if (r.mergedInto != null) continue;
    let col = null, wash = 0.1, band = 0.34;
    if (r.owner != null && !r.dead) col = G.players[r.owner].color;
    if (G.phase === 'draft') {
      if (r.id === myClaim()) { col = PCOL[UI.seat] || PCOL[0]; wash = 0.24; band = 0.6; }
      else if (r.id === UI.hover) { col = PCOL[UI.seat] || PCOL[0]; wash = 0.1; band = 0.3; }
      else { const claimer = G.seats && G.seats.findIndex(st => st.claim === r.id); if (claimer >= 0) { col = PCOL[claimer]; wash = 0.12; band = 0.4; } else { col = theme.ink; wash = 0.03; band = 0.14; } }   // every free region shows its edge, so it reads as something to pick
    }
    const path = PATHS.regions[r.id];
    if (col) {
      ctx.fillStyle = rgba(col, wash); ctx.fill(path, 'evenodd');
      ctx.save(); ctx.clip(path, 'evenodd'); ctx.strokeStyle = rgba(col, band); ctx.lineWidth = 16; ctx.stroke(path); ctx.restore();
    }
    if (r.dead) {
      ctx.save(); ctx.clip(path, 'evenodd');
      ctx.fillStyle = 'rgba(60,52,40,0.08)'; ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = 'rgba(60,52,40,0.13)'; ctx.lineWidth = 1.6;
      for (let k = -H; k < W; k += 14) { ctx.beginPath(); ctx.moveTo(k, 0); ctx.lineTo(k + H, H); ctx.stroke(); }
      ctx.restore();
    }
  }
  // city boundaries: a pale road of paper with a dashed ink rule down the middle, like a county line
  ctx.strokeStyle = rgba(theme.paper, 0.9); ctx.lineWidth = 5;
  for (const r of G.regions) if (r.mergedInto == null) ctx.stroke(PATHS.regions[r.id]);
  ctx.setLineDash([7, 5]); ctx.strokeStyle = rgba(theme.ink, 0.42); ctx.lineWidth = 1.3;
  for (const r of G.regions) if (r.mergedInto == null) ctx.stroke(PATHS.regions[r.id]);
  ctx.setLineDash([]);
  // the coast: a crisp inked edge with a lighter shore line just inside it
  ctx.save(); ctx.clip(PATHS.land, 'evenodd'); ctx.strokeStyle = rgba('#fffaf0', 0.55); ctx.lineWidth = 5; ctx.stroke(PATHS.land); ctx.restore();
  ctx.strokeStyle = rgba(theme.ink, 0.75); ctx.lineWidth = 1.8; ctx.stroke(PATHS.land);
  // labels: map lettering, wide-spaced capitals
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (const r of G.regions) {
    if (r.mergedInto != null) continue;
    if (G.phase === 'play' && r.owner == null) { ctx.fillStyle = rgba(theme.ink, 0.32); ctx.font = `italic 14px ${theme.mapFont}`; ctx.fillText('open country', r.c.x, r.c.y); }
    if (r.dead) { mapLabel('OVERRUN', r.c.x, r.c.y, 15, rgba(theme.ink, 0.45)); }
    // each city's name on its own land, in its colour
    const at = PATHS.labels[r.id];
    if (G.phase === 'play' && r.owner != null && !r.dead && at) { const p = G.players[r.owner]; mapLabel(p.name.toUpperCase(), at.x, at.y, 13, shade(p.color, 0.62)); }
  }
  // border crossings
  ctx.setLineDash([3, 6]); ctx.lineWidth = 2.5;
  for (const r of G.regions) for (const nb of r.nb) {
    if (nb.to < r.id) continue;
    const o = G.regions[nb.to].nb.find(n => n.to === r.id);
    const dead = r.dead || G.regions[nb.to].dead;
    ctx.strokeStyle = dead ? rgba(theme.dark ? '#ffffff' : '#000000', 0.08) : theme.ink2;
    ctx.globalAlpha = dead ? 1 : 0.55;
    ctx.beginPath(); ctx.moveTo(nb.gate.x, nb.gate.y); ctx.lineTo(o.gate.x, o.gate.y); ctx.stroke();
  }
  ctx.setLineDash([]); ctx.globalAlpha = 1;

  if (G.phase === 'draft') { drawDraftMarks(); return; }

  // lines
  ensureGeom();
  for (const p of G.players) {
    if (!p.alive) continue;
    for (const L of p.lines) {
      if (!L.geo || !L.geo.length) continue;
      ctx.strokeStyle = L.color; ctx.lineWidth = UI.selLine === L.id ? 7.5 : 6;
      tracePath(L.geo); ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;
  drawCrossings();
  // drag preview
  if (UI.drag && UI.drag.cur) {
    ctx.setLineDash([8, 8]); ctx.strokeStyle = theme.ink2; ctx.lineWidth = 5;
    ctx.beginPath(); ctx.moveTo(UI.drag.from.x, UI.drag.from.y); ctx.lineTo(UI.drag.cur.x, UI.drag.cur.y); ctx.stroke(); ctx.setLineDash([]);
  }
  // flights
  for (const f of G.flights) {
    if (f.t < 0) continue;   // still waiting its turn at the gate
    const x = f.x0 + (f.x1 - f.x0) * f.t, y = f.y0 + (f.y1 - f.y0) * f.t;
    shapePath(ctx, f.u.shape, x, y, 4.5); ctx.fillStyle = G.players[f.u.sender].color; ctx.fill();
  }
  // stations
  for (const s of G.stations.values()) drawStation(s, t);
  // trains
  for (const p of G.players) {
    if (!p.alive) continue;
    for (const L of p.lines) for (const T of L.trains) drawTrain(p, L, T);
  }
  // overrun bursts
  for (const f of UI.fx) for (let k = 0; k < 3; k++) {
    const tt = f.t - k * 0.25; if (tt < 0 || tt > 1.4) continue;
    ctx.beginPath(); ctx.arc(f.x, f.y, 20 + tt * 170, 0, Math.PI * 2);
    ctx.strokeStyle = f.color; ctx.globalAlpha = Math.max(0, 1 - tt / 1.4) * 0.8; ctx.lineWidth = 5 * (1 - tt / 1.6); ctx.stroke();
  }
  ctx.globalAlpha = 1;
  // placement ghost
  if (UI.placing && UI.mouse && (UI.placing.kind === 'warehouse' || UI.placing.kind === 'customs')) {
    // station upgrades: ring the station under the cursor if it can take this upgrade
    const st = upgradeTarget(me(), UI.mouse, UI.placing.kind);
    if (st) { ctx.beginPath(); ctx.arc(st.x, st.y, 28, 0, Math.PI * 2); ctx.strokeStyle = me().color; ctx.lineWidth = 4; ctx.globalAlpha = 0.7; ctx.stroke(); ctx.globalAlpha = 1; }
  } else if (UI.placing && UI.mouse) {
    const snap = snapToLine(me(), UI.mouse), at = snap ? snap.pos : UI.mouse;
    const ok = validSpot(me(), at);
    if (snap) {
      // highlight the stretch of line the depot will join
      ctx.strokeStyle = snap.L.color; ctx.globalAlpha = 0.35; ctx.lineWidth = 16;
      tracePath([snap.geo]); ctx.stroke();
    }
    ctx.globalAlpha = 0.6;
    shapePath(ctx, UI.placing.shape, at.x, at.y, 12); ctx.fillStyle = ok ? '#fff' : theme.danger; ctx.fill();
    ctx.strokeStyle = theme.ink; ctx.lineWidth = 3; ctx.stroke(); ctx.globalAlpha = 1;
  }
  // pops
  ctx.font = '700 14px -apple-system,system-ui,sans-serif';
  for (const pp of UI.pops) { ctx.globalAlpha = 1 - pp.t / 0.9; ctx.fillStyle = theme.ink; ctx.fillText(pp.text, pp.x, pp.y - pp.t * 22); }
  ctx.globalAlpha = 1;
}
function drawDraftMarks() {
  ctx.font = '600 14px -apple-system,system-ui,sans-serif'; ctx.textAlign = 'center';
  G.seats.forEach((seat, i) => {
    if (seat.claim == null) return;
    const r = G.regions[seat.claim];
    shapePath(ctx, shapeOf(i), r.c.x, r.c.y, 16); ctx.fillStyle = PCOL[i]; ctx.fill(); ctx.strokeStyle = theme.ink; ctx.lineWidth = 3; ctx.stroke();
    ctx.fillStyle = theme.ink; ctx.fillText(i === UI.seat ? 'your city' : seat.name, r.c.x, r.c.y + 34);
  });
  for (const r of G.regions) for (const nb of r.nb) { ctx.beginPath(); ctx.arc(nb.gate.x, nb.gate.y, 4, 0, Math.PI * 2); ctx.fillStyle = theme.ink2; ctx.fill(); }
}
function drawStation(s, t) {
  const p = G.players[s.owner], ink = theme.ink;
  const age = clamp((G.time - s.born) / 0.35, 0, 1), sc = 0.6 + 0.4 * age;
  // overcrowd ring
  if (s.crowd > 0) {
    const r = 26;
    ctx.beginPath(); ctx.arc(s.x, s.y, r, 0, Math.PI * 2); ctx.fillStyle = rgba(PCOL[0] === '' ? '#000' : '#d2553f', 0.10 + (s.units.length > capOf(s) ? 0.08 * (1 + Math.sin(t * 8)) : 0)); ctx.fill();
    ctx.beginPath(); ctx.arc(s.x, s.y, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * s.crowd); ctx.strokeStyle = theme.danger; ctx.lineWidth = 4; ctx.stroke();
  }
  if (s.warehouse) {
    // a wide loading platform under the station
    ctx.beginPath(); if (ctx.roundRect) ctx.roundRect(s.x - 25, s.y - 25, 50, 50, 12); else ctx.rect(s.x - 25, s.y - 25, 50, 50);
    ctx.fillStyle = rgba(theme.dark ? '#ffffff' : '#000000', 0.08); ctx.fill();
    ctx.strokeStyle = rgba(theme.dark ? '#ffffff' : '#000000', 0.25); ctx.lineWidth = 1.5; ctx.setLineDash([3, 3]); ctx.stroke(); ctx.setLineDash([]);
  }
  ctx.lineWidth = 3; ctx.strokeStyle = ink;
  if (s.kind === 'factory') {
    shapePath(ctx, s.shape, s.x, s.y, 17 * sc); ctx.fillStyle = p.color; ctx.fill(); ctx.stroke();
    if (s.graceUntil > G.time) {
      const left = s.graceUntil - G.time;
      ctx.beginPath(); ctx.arc(s.x, s.y, 27, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * left / CFG.surgeGrace);
      ctx.strokeStyle = p.color; ctx.lineWidth = 3; ctx.stroke();
      ctx.fillStyle = p.color; ctx.font = '700 11px -apple-system,system-ui,sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`surge ${Math.ceil(left)}s`, s.x, s.y + 40);
    }
    if (p.banking || p.bank > 0) {
      ctx.fillStyle = p.color; ctx.font = '700 11px -apple-system,system-ui,sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`bank ${p.bank}`, s.x, s.y - 26);
    }
    if (G.time > CFG.grace && p.rate > 0) { ctx.beginPath(); ctx.arc(s.x, s.y, 5, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * p.prod); ctx.strokeStyle = '#fff'; ctx.lineWidth = 2.5; ctx.stroke(); }
  } else if (s.kind === 'recycler') {
    shapePath(ctx, s.shape, s.x, s.y, 13 * sc); ctx.fillStyle = theme.dark ? '#2a2d33' : '#fffdf8'; ctx.fill(); ctx.stroke();
    shapePath(ctx, p.shape, s.x, s.y, 5); ctx.fillStyle = p.color; ctx.fill();
    // two chasing arcs: it turns what comes in around
    ctx.save(); ctx.translate(s.x, s.y); ctx.rotate(t * 1.5); ctx.strokeStyle = p.color; ctx.lineWidth = 2.5;
    for (const a0 of [0, Math.PI]) { ctx.beginPath(); ctx.arc(0, 0, 20, a0, a0 + Math.PI * 0.6); ctx.stroke(); }
    ctx.restore();
  } else if (s.kind === 'depot') {
    shapePath(ctx, s.shape, s.x, s.y, 13 * sc); ctx.fillStyle = theme.dark ? '#2a2d33' : '#fffdf8'; ctx.fill(); ctx.stroke();
    const q = G.players.find(q => q.shape === s.shape);
    shapePath(ctx, s.shape, s.x, s.y, 5); ctx.fillStyle = q ? q.color : ink; ctx.fill();
    if (p.isHuman && !p.adj.has(s.id)) {
      // a depot no line reaches can never receive anything: make that obvious
      ctx.save(); ctx.setLineDash([4, 4]); ctx.lineDashOffset = -t * 12;
      ctx.beginPath(); ctx.arc(s.x, s.y, 22, 0, Math.PI * 2); ctx.strokeStyle = theme.danger; ctx.lineWidth = 2; ctx.stroke(); ctx.restore();
      ctx.fillStyle = theme.danger; ctx.font = '600 11px -apple-system,system-ui,sans-serif'; ctx.textAlign = 'center';
      ctx.fillText('no line', s.x, s.y + 34);
    }
  } else {
    const closed = G.regions[s.to].dead;
    ctx.save(); ctx.translate(s.x, s.y); ctx.rotate(s.ang);
    ctx.beginPath(); if (ctx.roundRect) ctx.roundRect(-9, -15, 18, 30, 9); else ctx.rect(-9, -15, 18, 30);
    ctx.fillStyle = closed ? theme.paper2 : (theme.dark ? '#2a2d33' : '#fffdf8'); ctx.fill(); ctx.strokeStyle = closed ? theme.ink2 : ink; ctx.stroke();
    if (s.customs) {
      // customs: a booth stripe in the owner's colour across the gate
      ctx.beginPath(); if (ctx.roundRect) ctx.roundRect(-13, -19, 26, 38, 13); else ctx.rect(-13, -19, 26, 38);
      ctx.strokeStyle = p.color; ctx.lineWidth = 3; ctx.stroke();
    }
    ctx.beginPath();
    ctx.moveTo(-3, -6); ctx.lineTo(3, 0); ctx.lineTo(-3, 6);
    ctx.lineWidth = 2.5; ctx.stroke(); ctx.restore();
  }
  // waiting passengers
  const n = s.units.length, show = Math.min(n, 18);
  for (let i = 0; i < show; i++) {
    const u = s.units[i], col = i % 6, row = Math.floor(i / 6);
    const x = s.x + 24 + col * 11, y = s.y - 10 + row * 11, c = G.players[u.sender].color;
    shapePath(ctx, u.shape, x, y, 4.2);
    if (u.target === s.owner || u.sender === s.owner) { ctx.fillStyle = c; ctx.fill(); }
    else { ctx.strokeStyle = c; ctx.lineWidth = 1.6; ctx.stroke(); }   // hollow: just passing through
  }
  if (p.isHuman && n) {
    // say right at the queue why shapes are stuck
    const need = new Set(); let cut = false;
    for (const u of s.units) {
      if (u.target !== p.id || need.has(u.shape)) continue;
      if (!stationsOf(p).some(d => (d.kind === 'depot' || d.kind === 'recycler') && d.shape === u.shape)) need.add(u.shape);
      else if (dOf(distMap(p, 'd:' + u.shape), s.id) === Infinity) cut = true;
    }
    const names = [...need].map(x => NAMES[x].toLowerCase());
    const why = names.length ? `needs ${names.length > 1 ? names.join(' + ') + ' depots' : 'a ' + names[0] + ' depot'}` : cut ? 'no line to its depot' : null;
    if (why) {
      const rows = Math.ceil(Math.min(n, 18) / 6);
      ctx.fillStyle = theme.danger; ctx.font = '600 11px -apple-system,system-ui,sans-serif'; ctx.textAlign = 'left';
      ctx.fillText(why, s.x + 20, s.y - 4 + rows * 11 + (n > 18 ? 14 : 0)); ctx.textAlign = 'center';
    }
  }
  if (n > 18) { ctx.fillStyle = theme.ink2; ctx.font = '600 11px -apple-system,system-ui,sans-serif'; ctx.textAlign = 'left'; ctx.fillText('+' + (n - 18), s.x + 24, s.y + 24); ctx.textAlign = 'center'; }
}
function drawTrain(p, L, T) {
  if (L.st.length < 2) return;
  let x, y, ang;
  if (T.to == null) {
    const nx = lineNext(L, T.idx, T.dir) ?? lineNext(L, T.idx, -T.dir);
    if (nx == null) return;
    const a = S(L.st[T.idx]), sg = trainSeg(L, T.idx, nx), q = pointAt(sg.geo, sg.rev ? 1 : 0, sg.rev);
    x = a.x; y = a.y; ang = q.ang;
  } else {
    const k = ease(T.vt ?? T.t), sg = trainSeg(L, T.idx, T.to), q = pointAt(sg.geo, sg.rev ? 1 - k : k, sg.rev);
    x = q.x; y = q.y; ang = q.ang;
  }
  ctx.save(); ctx.translate(x, y); ctx.rotate(ang);
  const cars = Math.ceil((T.cap || CFG.trainCap) / CFG.carSeats), len = 10 + cars * 8, x0 = -len / 2;
  ctx.beginPath(); if (ctx.roundRect) ctx.roundRect(x0, -6, len, 12, 4); else ctx.rect(x0, -6, len, 12);
  ctx.fillStyle = L.color; ctx.fill();
  ctx.strokeStyle = theme.ink; ctx.lineWidth = 2.5; ctx.stroke();
  // carriage joints on longer trains
  if (cars > 2) {
    ctx.lineWidth = 1.2; ctx.globalAlpha = 0.5;
    for (let jx = x0 + 21; jx < len / 2 - 4; jx += 16) { ctx.beginPath(); ctx.moveTo(jx, -6); ctx.lineTo(jx, 6); ctx.stroke(); }
    ctx.globalAlpha = 1;
  }
  for (let i = 0; i < T.units.length; i++) {
    const u = T.units[i], cx = x0 + 6 + Math.floor(i / 2) * 5, cy = i % 2 ? 3 : -3;
    const col = G.players[u.sender].color;
    shapePath(ctx, u.shape, cx, cy, 2.7);
    if (u.target === p.id || u.sender === p.id) { ctx.fillStyle = col; ctx.fill(); ctx.strokeStyle = shade(col, 0.55); ctx.lineWidth = 0.9; ctx.stroke(); }
    else { ctx.fillStyle = shade(col, 0.35); ctx.fill(); ctx.strokeStyle = col; ctx.lineWidth = 1.2; ctx.stroke(); }
  }
  ctx.restore();
}


// ---------- shape icons for the HUD ----------
export function shapeSVG(shape, color, size = 16) {
  const r = 6, c = 8;
  let d = '';
  const pts = (n, rr, rot, cy = c) => Array.from({ length: n }, (_, i) => { const a = rot + i * Math.PI * 2 / n; return `${(c + Math.cos(a) * rr).toFixed(2)},${(cy + Math.sin(a) * rr).toFixed(2)}`; }).join(' ');
  let el;
  if (shape === 'circle') el = `<circle cx="8" cy="8" r="${r}"/>`;
  else if (shape === 'square') el = `<rect x="2.6" y="2.6" width="10.8" height="10.8"/>`;
  else if (shape === 'triangle') el = `<polygon points="${pts(3, 7.2, -Math.PI / 2, 9)}"/>`;
  else if (shape === 'pentagon') el = `<polygon points="${pts(5, 6.6, -Math.PI / 2, 8.4)}"/>`;
  else if (shape === 'diamond') el = `<polygon points="${pts(4, 7, -Math.PI / 2)}"/>`;
  else if (shape === 'hexagon') el = `<polygon points="${pts(6, 6.6, 0)}"/>`;
  else if (shape === 'cross') el = `<polygon points="5.8,1.8 10.2,1.8 10.2,5.8 14.2,5.8 14.2,10.2 10.2,10.2 10.2,14.2 5.8,14.2 5.8,10.2 1.8,10.2 1.8,5.8 5.8,5.8"/>`;
  else if (shape === 'heart') el = `<path d="M8 14.2 C2 9 1.6 4.2 5 3.2 C6.6 2.8 7.6 3.8 8 5 C8.4 3.8 9.4 2.8 11 3.2 C14.4 4.2 14 9 8 14.2 Z"/>`;
  else if (shape === 'crescent') el = `<path d="M10.6 2.4 A6.4 6.4 0 1 0 13.6 11.4 A5.2 5.2 0 1 1 10.6 2.4 Z"/>`;
  else { const s = Array.from({ length: 10 }, (_, i) => { const a = -Math.PI / 2 + i * Math.PI / 5, rr = i % 2 ? 3.3 : 7.4; return `${(c + Math.cos(a) * rr).toFixed(2)},${(8.5 + Math.sin(a) * rr).toFixed(2)}`; }).join(' '); el = `<polygon points="${s}"/>`; }
  return `<svg width="${size}" height="${size}" viewBox="0 0 16 16" style="flex:none"><g fill="${color}" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round">${el}</g></svg>`;
}


// a tile of paper grain, made once and repeated over the land
let GRAIN = null;
function grain() {
  if (GRAIN) return GRAIN;
  const c = document.createElement('canvas'); c.width = c.height = 128;
  const x = c.getContext('2d'), img = x.createImageData(128, 128), r = mulberry(11);
  for (let i = 0; i < img.data.length; i += 4) { const v = r(); img.data[i] = img.data[i + 1] = img.data[i + 2] = v < 0.5 ? 60 : 255; img.data[i + 3] = 9 + v * 10; }
  x.putImageData(img, 0, 0);
  return (GRAIN = ctx.createPattern(c, 'repeat'));
}
// lettering the way printed maps do it: spaced capitals with a paper halo
function mapLabel(text, x, y, size, color) {
  size = Math.max(size, 10 / view.s);   // never smaller than 10 screen pixels, however far out you zoom
  ctx.font = `600 ${size}px ${theme.mapFont}`;
  if ('letterSpacing' in ctx) ctx.letterSpacing = `${(size * 0.18).toFixed(1)}px`;
  ctx.lineWidth = 3.5; ctx.strokeStyle = rgba(theme.paper, 0.85); ctx.strokeText(text, x, y);
  ctx.fillStyle = color; ctx.fillText(text, x, y);
  if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
}

// ---------- the front page: every shape riding the tram lines, round and round ----------
const TITLE = { key: '', lines: [] };
const LIVERY = ['#c2412d', '#1e6b52', '#caa24c', '#2f6f9a', '#7a2433', '#3c8a7a'];
function titleLines() {
  const key = innerWidth + 'x' + innerHeight;
  if (TITLE.key === key) return TITLE.lines;
  TITLE.key = key;
  const w = innerWidth, h = innerHeight, n = innerWidth < 760 ? 5 : 6, rnd = mulberry(7);
  TITLE.lines = Array.from({ length: n }, (_, k) => {
    // a gentle S across the screen, each line at its own height and swing, all sampled into a polyline
    const y0 = h * (0.1 + 0.8 * (k + 0.5) / n), amp = h * (0.05 + rnd() * 0.07), ph = rnd() * 6.28, f = 1.2 + rnd() * 1.4;
    const pts = [];
    for (let i = 0; i <= 120; i++) { const x = -60 + (w + 120) * i / 120; pts.push({ x, y: y0 + amp * Math.sin(ph + f * Math.PI * i / 120) + amp * 0.35 * Math.sin(ph * 2 + 3.1 * f * Math.PI * i / 120) }); }
    const len = [0]; for (let i = 1; i < pts.length; i++) len.push(len[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
    const total = len[len.length - 1], cars = Math.max(2, Math.round(total / 420));
    return { pts, len, total, color: LIVERY[k % LIVERY.length], dir: k % 2 ? -1 : 1, speed: 38 + rnd() * 26, stops: Math.round(total / 230),
      cars: Array.from({ length: cars }, (_, c) => ({ at: (c + rnd() * 0.5) / cars, first: k * 3 + c * 2 })) };
  });
  return TITLE.lines;
}
function mulberry(a) { return () => { a |= 0; a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
function along(L, d) {
  d = ((d % L.total) + L.total) % L.total;
  let i = 1; while (i < L.len.length - 1 && L.len[i] < d) i++;
  const a = L.pts[i - 1], b = L.pts[i], f = (d - L.len[i - 1]) / Math.max(1e-6, L.len[i] - L.len[i - 1]);
  return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, ang: Math.atan2(b.y - a.y, b.x - a.x) };
}
function drawTitle(t) {
  const w = innerWidth, h = innerHeight;
  ctx.fillStyle = theme.paper; ctx.fillRect(0, 0, w, h);
  // a faint survey grid, like the back of a route map
  ctx.strokeStyle = 'rgba(31,42,36,0.05)'; ctx.lineWidth = 1;
  for (let x = 0; x < w; x += 48) { ctx.beginPath(); ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, h); ctx.stroke(); }
  for (let y = 0; y < h; y += 48) { ctx.beginPath(); ctx.moveTo(0, y + 0.5); ctx.lineTo(w, y + 0.5); ctx.stroke(); }
  ctx.lineJoin = 'round'; ctx.lineCap = 'round';
  const lines = titleLines();
  for (const L of lines) {
    ctx.beginPath(); L.pts.forEach((q, i) => (i ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y)));
    ctx.strokeStyle = 'rgba(31,42,36,0.18)'; ctx.lineWidth = 13; ctx.stroke();
    ctx.strokeStyle = L.color; ctx.lineWidth = 8; ctx.stroke();
    // stops: white pills with a dark ring
    for (let k = 0; k < L.stops; k++) {
      const q = along(L, (k + 0.5) * L.total / L.stops);
      ctx.beginPath(); ctx.arc(q.x, q.y, 8.5, 0, Math.PI * 2); ctx.fillStyle = '#fffaf0'; ctx.fill(); ctx.strokeStyle = theme.ink; ctx.lineWidth = 3; ctx.stroke();
    }
  }
  for (const L of lines) for (const C of L.cars) {
    const d = L.dir * t * L.speed + C.at * L.total;
    // a stream of loose shapes trailing each tram, every shape in turn, repeats and all
    for (let k = 1; k <= 4; k++) {
      const q = along(L, d - L.dir * (34 + k * 22)), sh = SHAPES[(C.first + k) % SHAPES.length];
      shapePath(ctx, sh, q.x, q.y, 6.5); ctx.fillStyle = PCOL[(C.first + k * 3) % PCOL.length]; ctx.fill(); ctx.strokeStyle = theme.ink; ctx.lineWidth = 1.6; ctx.stroke();
    }
    // the tram: a car in the line's livery with a cream window band, carrying two shapes
    const q = along(L, d);
    ctx.save(); ctx.translate(q.x, q.y); ctx.rotate(q.ang + (L.dir < 0 ? Math.PI : 0));
    ctx.beginPath(); ctx.roundRect(-26, -10, 52, 20, 7); ctx.fillStyle = L.color; ctx.fill(); ctx.strokeStyle = theme.ink; ctx.lineWidth = 2.5; ctx.stroke();
    ctx.beginPath(); ctx.roundRect(-20, -5.5, 40, 11, 3); ctx.fillStyle = '#fbf3dc'; ctx.fill();
    for (let k = 0; k < 2; k++) {
      shapePath(ctx, SHAPES[(C.first + 5 + k) % SHAPES.length], -9 + k * 18, 0, 4.4);
      ctx.fillStyle = PCOL[(C.first + k * 7) % PCOL.length]; ctx.fill(); ctx.strokeStyle = theme.ink; ctx.lineWidth = 1.1; ctx.stroke();
    }
    ctx.restore();
  }
}
